import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useLocation, useParams } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import {
  getSelectedBp,
  selectHasAccessToBp,
  selectCurrentBpHasTaskManagement
} from 'features/bpDetails/ducks/selectors'
import { FOLDERS } from 'utils/bpData'
import BackLink from 'components/BackLink'
import { floatingButtonStyle } from 'components/atoms/FloatingButton'
import { useFloatingSlot, FLOATING_PRIORITY } from 'components/atoms/floatingStack'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { pathOr, propOr } from 'ramda'
import Tooltip from '@mui/material/Tooltip'
import { ReactComponent as CommentIcon } from 'assets/images/addComment.svg'
import AddMultipleFilesModal from 'features/bpDetails/Components/files/AddMultipleFilesModal'
import { ReactComponent as FloorplanIcon } from 'assets/images/addFloorplan.svg'
import AnalyticsBox from 'features/bpDetails/Components/AnalyticsBox'
import CompliancePulse from 'features/bpDetails/Components/CompliancePulse'
import NewRemedialsCard from 'features/bpDetails/Components/NewRemedialsCard'
import Floorplans from 'features/bpDetails/Components/Floorplans'
import UnifiedFiles from 'features/bpDetails/Components/UnifiedFiles'
import ModelPanel from 'features/bpDetails/Components/ModelPanel'
import NotesPanel from 'features/bpDetails/Components/NotesPanel'
import AccessManager from 'features/accessControl/AccessManager'
import BuildingNamesModal from 'features/bpDetails/Components/BuildingNamesModal'
import SellOutlinedIcon from '@mui/icons-material/SellOutlined'
import UnitsPanel from 'features/bpDetails/Components/units/UnitsPanel'
import BpInfo from 'features/bpDetails/Components/BpInfo/BpInfo'
import styled, { css } from 'styled-components'
import { getCurrentUser, selectComplianceEnabled, selectFeatureVisibility, selectFeatureDefaultOwner } from 'features/auth/ducks/selectors'
import { canEditBp } from 'utils/user'
import { fetchFavouritesRoutine } from 'features/bpList/ducks/actions'
import ShortlistButton from 'features/bpDetails/Components/ShortlistButton'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import { getPortfoliosForBp } from 'services/portfolioService'
import AddNoteModal from 'features/bpDetails/Components/AddNoteModal'
import {
  fetchBpRoutine,
  bpPeopleCountRoutine,
  markBpAsViewedRoutine
} from 'features/bpDetails/ducks/actions'
import { isNotNilOrEmpty } from 'utils/ramda'
import {
  getBpFilesCountsRoutine,
  getFileTagsForBpRoutine
} from 'ducks/files/actions'
import { fetchBpUnitsRoutine, clearSelectedUnitRoutine } from 'ducks/units/actions'
import NoticeBoard from 'features/bpDetails/Components/units/NoticeBoard'
import ImageViewer from 'components/ImageViewer'
import { useNavigate, useSearchParams } from 'react-router-dom'
import BPGuru from 'features/bpDetails/Components/BPGuru'
import ReportingHub from 'features/bpDetails/Components/reporting/ReportingHub'
import InboxTab from 'components/BuildingPassport/Inbox/InboxTab'
import { TaskReports } from 'features/taskManagement'
import RemedialsWorkspace from 'features/actions/workspace/RemedialsWorkspace'
import IncidentsPage from 'features/incidents/IncidentsPage'
import WikiPage from 'features/bpDetails/wiki/WikiPage'
import PlanningConstraintsPanel from 'features/bpDetails/Components/planning/PlanningConstraintsPanel'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CookieService from 'services/CookieService'
import { useMediaQuery } from '@mui/material'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined'
import ActionsAPI from 'services/ActionsAPI'
import AddressUnitsSyncPrompt from 'features/bpDetails/Components/units/AddressUnitsSyncPrompt'
import FeatureSwitchBar from 'components/FeatureSwitchBar'
import { isFeatureVisible } from 'constants/featureGroups'
import TabNav from 'components/TabNav'
import { keepStripPinned } from 'components/TabNav/keepStripPinned'
import PassportDetailSkeleton from 'components/PassportDetailSkeleton'
import PassportHeader, {
  PassportAction as ActionChip,
  PassportIconAction as ShortlistChip,
  PassportOverviewIntro,
} from 'components/PassportHeader'
import { getBpUnitsBpId, getBpUnitsCount } from 'ducks/units/selectors'
import LocationCard from 'components/LocationCard'
import { getBpUsers, getBpAliases } from 'services/BpService'
import ComplianceWorkspace from 'features/compliance/ComplianceWorkspace'
import { selectHasReportingAccess } from 'ducks/reporting/selectors'
import { checkReportingAccessRoutine } from 'ducks/reporting/actions'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import AssistantOutlinedIcon from '@mui/icons-material/AssistantOutlined'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import SiteRisksIcon from '@mui/icons-material/GppMaybeOutlined'
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import { notifyFilesRefresh, useUploadFollowUp } from 'utils/uploadEvents'
import { countActiveThisWeek, visibleAccessRows } from 'features/accessControl/visibleUsers'
import { documentCountSummary } from 'utils/documentCounts'

const BpDetails = () => {
  const { folder, id } = useParams()
  const dispatch = useDispatch()
  const bp = useSelector(getSelectedBp)
  const hasFullAccess = useSelector(selectHasAccessToBp)
  const currentBpHasTaskManagement = useSelector(
    selectCurrentBpHasTaskManagement
  )
  const currentUser = useSelector(getCurrentUser)
  const complianceEnabled = useSelector(selectComplianceEnabled)
  const featureVisibility = useSelector(selectFeatureVisibility)
  const featureOwner = useSelector(selectFeatureDefaultOwner)
  const hasReportingAccess = useSelector(selectHasReportingAccess)
  const storedFilesCounts = useSelector(state => state.files.filesCounts)
  const filesCounts = storedFilesCounts?.bpId == null || String(storedFilesCounts.bpId) === String(id) ? storedFilesCounts : null
  const bpUnitsCount = useSelector(getBpUnitsCount)
  const bpUnitsBpId = useSelector(getBpUnitsBpId)
  const unitsCount = bpUnitsBpId != null && String(bpUnitsBpId) === String(id) ? bpUnitsCount : undefined
  const [people, setPeople] = useState(null)
  const [namesOpen, setNamesOpen] = useState(false)
  const [bpAliases, setBpAliases] = useState([])

  const currentUserId = currentUser?.id ?? null
  const updatePeople = useCallback(rows => {
    const visible = visibleAccessRows(rows, currentUserId)
    setPeople({ total: visible.length, active: countActiveThisWeek(visible) })
    dispatch(bpPeopleCountRoutine.success({ bpId: id, total: visible.length }))
  }, [currentUserId, dispatch, id])

  const accessVersion = useSelector(state => state.bpDetails.accessVersion || 0)
  useEffect(() => {
    if (!id || !hasFullAccess) return undefined
    let cancelled = false
    getBpUsers(id)
      .then(res => { if (!cancelled) updatePeople(res?.data?.data || []) })
      .catch(() => { if (!cancelled) setPeople(null) })
    return () => { cancelled = true }
  }, [id, hasFullAccess, updatePeople, accessVersion])

  useEffect(() => {
    setBpAliases(Array.isArray(bp?.aliases) ? bp.aliases : [])
  }, [bp?.aliases])

  const isFrs = currentUser.role === 'emergency_service'
  const canEdit = canEditBp(currentUser, bp)
  const location = useLocation()
  const { pathname } = location
  const [searchParams] = useSearchParams()
  const tabsRef = useRef(null)
  const complianceSection = searchParams.get('section') || 'overview'
  const loaded = bp?.id === id
  const isMobile = useMediaQuery('(max-width:768px)')
  const [isRightPanelExpanded, setIsRightPanelExpanded] = useState(
    () => !isMobile
  )
  const [syncUnitsRequestKey, setSyncUnitsRequestKey] = useState(0)
  const [syncUnitsRequestPayload, setSyncUnitsRequestPayload] = useState(null)
  const isActiveBp = propOr('', 'status', bp) === 'active'
  const infoSlot = useFloatingSlot('bp-info', FLOATING_PRIORITY.info, !isRightPanelExpanded)

  const [bpPortfolios, setBpPortfolios] = useState([])
  const [openActionStats, setOpenActionStats] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (bp?.id) {
      getPortfoliosForBp(bp.id)
        .then(res => setBpPortfolios(res.data.data || []))
        .catch(() => {})
    }
  }, [bp?.id])

  // Lightweight action-stats fetch — surfaces a chip near the BP header so
  // users can spot work waiting for them without opening Actions.
  useEffect(() => {
    if (!bp?.id || !currentBpHasTaskManagement) return
    let cancelled = false
    ActionsAPI.stats({ building_passport_id: bp.id })
      .then(res => { if (!cancelled) setOpenActionStats(res?.data?.data || null) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [bp?.id, currentBpHasTaskManagement])

  const isAnalytics = pathOr('', ['state', 'path'], location) === 'analytics'
  const isDraft = pathname.includes('drafts')
  const isFavourite = pathname.includes('favourites')

  const isFrsUser = currentUser.role === 'emergency_service'
  const isResponsiblePerson = currentUser.role === 'responsible_person'

  const handleCreateNewBp = () => {
    if (window.location.pathname.includes('create-bp')) {
      window.location.reload()
    } else {
      redirect(PRIVATE_PATHS.createBp)
    }
  }

  useEffect(() => {
    if (isMobile && isRightPanelExpanded) {
      const originalStyle = window.getComputedStyle(document.body).overflow
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'

      return () => {
        document.body.style.overflow = originalStyle
        document.documentElement.style.overflow = ''
      }
    } else {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
    }
  }, [isMobile, isRightPanelExpanded])

  useEffect(() => {
    if (id && bp?.id !== id) {
      dispatch(fetchBpRoutine({ id }))
      dispatch(fetchBpUnitsRoutine({ bpId: id }))
    }
  }, [id, bp?.id])

  useEffect(() => {
    if (id) dispatch(getFileTagsForBpRoutine({ bpId: id }))
  }, [id])

  useEffect(() => {
    if (id && hasFullAccess) dispatch(markBpAsViewedRoutine({ bpId: id }))
  }, [id, hasFullAccess])

  useEffect(() => {
    dispatch(fetchFavouritesRoutine({ page: 1 }))
  }, [])

  useEffect(() => {
    if (bp?.id) dispatch(getBpFilesCountsRoutine({ id: bp.id }))
  }, [bp?.id])

  const filesCountsRef = useRef(storedFilesCounts)
  filesCountsRef.current = storedFilesCounts
  useUploadFollowUp(bp?.id, {
    onTick: () => {
      dispatch(getBpFilesCountsRoutine({ id: bp.id }))
      notifyFilesRefresh(bp.id)
    },
    readTotal: () => {
      const counts = filesCountsRef.current
      if (!counts || (counts.bpId != null && String(counts.bpId) !== String(bp?.id))) return 0
      return counts.totalWithDuplicates ?? counts.total ?? 0
    }
  })

  // Auto-collapse right panel after 7 seconds
  useEffect(() => {
    if (isMobile) return

    const timer = setTimeout(() => {
      setIsRightPanelExpanded(false)
    }, 7000)

    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    setIsRightPanelExpanded(!isMobile)
  }, [isMobile])

  const backButtonDetails = useMemo(() => {
    switch (true) {
      case isAnalytics:
        return { label: 'Back to checklists', path: PRIVATE_PATHS.analytics }
      case isDraft:
        return { label: 'Back to drafts', path: PRIVATE_PATHS.drafts }
      case isFavourite:
        return {
          label: 'Back to favourites',
          path: PRIVATE_PATHS.savedPassports
        }
      default:
        return {
          label: 'Back to my Building Passports',
          path: PRIVATE_PATHS.bpList
        }
    }
  }, [isAnalytics, isDraft, isFavourite])

  useEffect(() => {
    if (!FOLDERS.includes(folder) && isNotNilOrEmpty(hasFullAccess)) {
      redirect(`${backButtonDetails.path}/${id}/${hasFullAccess ? 'guru' : 'units'}`)
    }
  }, [folder, hasFullAccess])

  useEffect(() => {
    if (id && hasFullAccess) {
      dispatch(checkReportingAccessRoutine.trigger({ bpId: id }))
    }
  }, [id, hasFullAccess, dispatch])

  const handleFolderClick = key => {
    CookieService.setLastFolder(id, key)
    redirect(`${backButtonDetails.path}/${id}/${key}`)
  }

  const handleFileCountClick = () => {
    CookieService.setLastFolder(id, 'other')
    CookieService.set(`selectAllFiles_${id}`, 'true')
    redirect(`${backButtonDetails.path}/${id}/other`)
  }

  const activeTabKey = useMemo(() => {
    const aliases = { files: 'other', insuranceGuru: 'reporting', floorplans: 'frs' }
    return aliases[folder] || folder
  }, [folder])

  const tabItems = useMemo(() => {
    const items = []
    const showTasks = isFeatureVisible(featureVisibility, 'tasks', featureOwner)

    if (hasFullAccess) {
      items.push({
        key: 'wiki',
        label: 'Overview',
        icon: MenuBookIcon,
        hint: 'Plain-English overview of this building \u2014 facts, legislation, risk, compliance and recent activity, with cited sources.'
      })
      items.push({
        key: 'guru',
        label: 'Guru',
        icon: AssistantOutlinedIcon,
        badge: 'New',
        hint: 'AI-powered document analysis and insights for your building files'
      })
      items.push({
        key: 'reporting',
        label: 'Reports',
        icon: AssessmentOutlinedIcon,
        badge: 'Beta',
        disabled: !hasReportingAccess,
        hint: hasReportingAccess
          ? 'AI-powered reports \u2014 insurance submissions, CPSE.7, and more'
          : 'Access required \u2014 contact your administrator'
      })
      items.push({
        key: 'planning',
        label: 'Site Risks',
        icon: SiteRisksIcon,
        badge: 'Beta',
        dataTourId: 'folders.site-risks',
        hint: 'Planning and environmental risks for this building, such as conservation areas, listed buildings and flood zones.'
      })
    }

    if (isFrs) {
      items.push({
        key: 'frs',
        label: 'FRS Documents',
        icon: LocalFireDepartmentOutlinedIcon,
        hint: 'Fire and Rescue Service documents and emergency service related files'
      })
    }

    if (hasFullAccess) {
      items.push({
        key: 'other',
        label: 'Documents',
        icon: FolderOutlinedIcon,
        count: filesCounts?.total ? filesCounts.total.toLocaleString('en-GB') : undefined,
        hint: documentCountSummary(filesCounts)?.hidden > 0
          ? `${documentCountSummary(filesCounts).text}. General building documents, certificates, plans and other files.`
          : 'General building documents, certificates, plans, and documentation',
        onCountClick: handleFileCountClick
      })
      items.push({
        key: 'accessControl',
        label: 'Access',
        icon: GroupOutlinedIcon,
        count: people?.total ? people.total.toLocaleString('en-GB') : undefined,
        hint: people?.total
          ? `${people.total.toLocaleString('en-GB')} ${people.total === 1 ? 'person can' : 'people can'} see this Building Passport${bp?.isOwner && people.active ? `, ${people.active} active this week` : ''}. Manage who has access and what they can do.`
          : 'Manage user permissions and access control for Building Passport data'
      })
      items.push({
        key: 'inbox',
        label: 'Inbox',
        icon: EmailOutlinedIcon,
        hint: 'Email inbox for Building Passport communications and attachments'
      })
      if (complianceEnabled && currentBpHasTaskManagement) {
        items.push({
          key: 'compliance',
          dataTour: 'passport-tab-compliance',
          dataTourId: 'passport.tab-compliance',
          label: 'Compliance',
          icon: AssignmentTurnedInOutlinedIcon,
          hint: 'Compliance score, obligations awaiting evidence and documents to approve for this Building Passport'
        })
      }
      if (currentBpHasTaskManagement && showTasks) {
        items.push({
          key: 'taskReports',
          label: 'Task Reports',
          icon: DescriptionOutlinedIcon,
          hint: 'PDF reports generated from completed tasks'
        })
      }
    }

    items.push({
      key: 'units',
      label: 'Units',
      icon: ApartmentOutlinedIcon,
      count: Number(unitsCount) > 0 ? Number(unitsCount).toLocaleString('en-GB') : undefined,
      hint: 'Individual residential or commercial units within the building',
      onActivate: () => {
        handleFolderClick('units')
        dispatch(clearSelectedUnitRoutine())
      }
    })

    items.push({
      key: 'calendar',
      label: 'Calendar',
      icon: EventOutlinedIcon,
      hint: 'Scheduled work and deadlines for this building',
      onActivate: () => redirect(`/calendar?bp=${id}`)
    })

    if (!hasFullAccess) {
      items.push({
        key: 'noticeBoard',
        label: 'Notice Board',
        icon: CampaignOutlinedIcon,
        hint: 'Public notices and announcements for building residents'
      })
    }

    return items
  }, [
    hasFullAccess, isFrs, complianceEnabled, currentBpHasTaskManagement,
    featureVisibility, featureOwner, hasReportingAccess, filesCounts, id, dispatch,
    people, unitsCount, bp?.isOwner
  ])

  const folderContent = useMemo(() => {
    switch (true) {
      case folder === 'guru' && hasFullAccess:
        return <BPGuru />
      case folder === 'reporting' && hasFullAccess:
      case folder === 'insuranceGuru' && hasFullAccess:
        return <ReportingHub />
      case (folder === 'floorplans' || folder === 'frs') && hasFullAccess:
        return <Floorplans />
      case folder === 'other' && hasFullAccess:
        return <UnifiedFiles />
      case folder === 'files' && hasFullAccess:
        return <UnifiedFiles />
      case folder === 'model' && hasFullAccess:
        return <ModelPanel />
      case folder === 'notes' && hasFullAccess:
        return <NotesPanel />
      case folder === 'accessControl' && hasFullAccess:
        return <MainContentPanel><AccessManager scopeType='building' scopeId={id} scopeName={bp?.name || 'this Building Passport'} canManage={canEditBp(currentUser, bp)} isOwner={!!bp?.isOwner} bp={bp} onUsersChanged={updatePeople} /></MainContentPanel>
      case folder === 'inbox' && hasFullAccess:
        return <InboxTab />
      case folder === 'taskReports' &&
        hasFullAccess &&
        currentBpHasTaskManagement:
        return <TaskReports buildingPassportId={id} />
      case folder === 'actions' && hasFullAccess && currentBpHasTaskManagement:
        return <RemedialsWorkspace scope={{ kind: 'building', id }} />
      case folder === 'incidents' && hasFullAccess && currentBpHasTaskManagement:
        return <IncidentsPage buildingPassportId={id} />
      case folder === 'compliance' && hasFullAccess && currentBpHasTaskManagement:
        return <ComplianceWorkspace bpId={id} initialSection={complianceSection} />
      case folder === 'wiki' && hasFullAccess:
        return (
          <OverviewStack>
            <PassportOverviewIntro
              highlight={!isDraft && currentBpHasTaskManagement ? (
                <CompliancePulse
                  compact
                  bpId={id}
                  showCompliance={complianceEnabled}
                  showTasks={isFeatureVisible(featureVisibility, 'tasks', featureOwner)}
                  showIncidents={isFeatureVisible(featureVisibility, 'incidents', featureOwner)}
                />
              ) : null}
              fields={[
                bpPortfolios.length ? {
                  label: bpPortfolios.length === 1 ? 'Portfolio' : 'Portfolios',
                  emblem: 'portfolio',
                  value: bpPortfolios.map(pf => pf.name).join(', '),
                  onClick: () => navigate(PRIVATE_PATHS.portfolioDetail.replace(':id', bpPortfolios[0].id)),
                  title: 'Open the portfolio this building belongs to'
                } : null,
              ]}
            />
            <LocationCard
              name={bp.name}
              street={bp.street}
              city={bp.city}
              postalCode={bp.postalCode}
              uprn={bp.uprn}
            />
            {!isDraft && currentBpHasTaskManagement && isFeatureVisible(featureVisibility, 'tasks', featureOwner) && (
              <NewRemedialsCard bpId={id} />
            )}
            {!isDraft && isFeatureVisible(featureVisibility, 'checklists', featureOwner) && (
              <AnalyticsBox
                bp={bp}
                isRightPanelExpanded={isRightPanelExpanded}
              />
            )}
            <WikiPage buildingPassport={bp} />
          </OverviewStack>
        )
      case folder === 'analytics' && hasFullAccess:
        return (
          <OverviewStack>
            <AnalyticsBox bp={bp} isRightPanelExpanded={isRightPanelExpanded} />
          </OverviewStack>
        )
      case folder === 'planning' && hasFullAccess:
        return <PlanningConstraintsPanel />
      case folder === 'noticeBoard':
        return <NoticeBoard />
      case folder === 'units':
        return (
          <UnitsPanel
            redirectPath={backButtonDetails.path}
            onRequestSyncUnits={payload => {
              setSyncUnitsRequestPayload(payload || null)
              setSyncUnitsRequestKey(key => key + 1)
            }}
          />
        )
      default:
        return null
    }
  }, [
    folder,
    complianceSection,
    hasFullAccess,
    currentBpHasTaskManagement,
    isDraft,
    isActiveBp,
    bpPortfolios,
    complianceEnabled,
    featureVisibility,
    featureOwner,
    updatePeople,
    id,
    bp,
    currentUser,
    isRightPanelExpanded,
    backButtonDetails.path
  ])
  return !loaded ? (
    <PageWrapper isRightPanelExpanded={false}>
      <ContentWrapper>
        <PassportDetailSkeleton ariaLabel='Loading this Building Passport' />
      </ContentWrapper>
    </PageWrapper>
  ) : (
    <PageWrapper isRightPanelExpanded={isRightPanelExpanded}>
      <ImageViewer />
      <ContentWrapper>
        <BackButtonWrapper>
          <BackLink onClick={() => redirect(backButtonDetails.path)}>
            {backButtonDetails.label}
          </BackLink>
          {propOr('', 'status', bp) === 'draft' && (
            <Tooltip title='This Building Passport is still in draft...'>
              <DraftLabel>Draft</DraftLabel>
            </Tooltip>
          )}
        </BackButtonWrapper>

            <PassportHeader
              kind='building'
              name={bp.name}
              subtitle={[bp.street, bp.city, bp.postalCode].filter(Boolean).join(', ')}
              onSelectBp={target => redirect(`${backButtonDetails.path}/${target.id}/${folder || 'wiki'}`)}
              actions={hasFullAccess ? (
                <>
                  {currentBpHasTaskManagement && (
                    <ActionChip
                      as='button'
                      type='button'
                      onClick={() => navigate(`${PRIVATE_PATHS.dashboard}?altitude=bp&entityId=${bp.id}`)}
                      title='See this Building Passport on the compliance dashboard'
                    >
                      <SpaceDashboardOutlinedIcon style={{ fontSize: 16 }} /> Dashboard
                    </ActionChip>
                  )}
                  {canEdit && (
                    <ActionChip
                      as='button'
                      type='button'
                      data-tour-id='buildingNames.open'
                      onClick={() => setNamesOpen(true)}
                      title='Aliases are names this building is known by in documents, such as a block reference or an older project name'
                    >
                      <SellOutlinedIcon style={{ fontSize: 16 }} /> Aliases
                    </ActionChip>
                  )}
                  {canEdit && (
                  <>
                  <AddNoteModal>
                    <ActionChip><CommentIcon /> Note</ActionChip>
                  </AddNoteModal>
                  <AddMultipleFilesModal method='ai'>
                    <ActionChip><FloorplanIcon /> File</ActionChip>
                  </AddMultipleFilesModal>
                  <ShortlistChip><ShortlistButton /></ShortlistChip>
                  </>
                  )}
                </>
              ) : null}
              toolbar={hasFullAccess && currentBpHasTaskManagement
                ? <FeatureSwitchBar surface='passport' isOwner={featureOwner} compact />
                : null}
              meta={[
                bp.uprn ? `UPRN ${bp.uprn}` : null,
                bpAliases.length > 0
                  ? `Aliases: ${bpAliases.map(a => a.name).join(', ')}`
                  : null,
              ].filter(Boolean)}
              complianceScope={hasFullAccess && complianceEnabled && currentBpHasTaskManagement ? { bpId: bp.id } : null}
              onOpenCompliance={() => handleFolderClick('compliance')}
            />

        <ContentCard>
          <TabsWrapper ref={tabsRef} data-tour='passport-tabs' data-tour-id='passport.tabs'>
            <TabNav
              ariaLabel='Building Passport sections'
              items={tabItems}
              activeKey={activeTabKey}
              onSelect={key => keepStripPinned(tabsRef.current, () => handleFolderClick(key))}
            />
          </TabsWrapper>
          <MainContent>{folderContent}</MainContent>
        </ContentCard>
      </ContentWrapper>

      {namesOpen && (
        <BuildingNamesModal
          bpId={id}
          bpName={bp.name}
          canManage={canEdit}
          onClose={() => setNamesOpen(false)}
          onChanged={() => {
            getBpAliases({ bpId: id })
              .then(res => setBpAliases(res?.data?.data || []))
              .catch(() => {})
          }}
        />
      )}

      {!isRightPanelExpanded && !infoSlot.hidden && (
        <MobileInfoTrigger
          style={infoSlot.style}
          type='button'
          aria-label='Building details'
          onClick={() => setIsRightPanelExpanded(true)}
        >
          <InfoOutlinedIcon />
        </MobileInfoTrigger>
      )}

      <AddressUnitsSyncPrompt
        bp={bp}
        enabled={!isDraft && isActiveBp && hasFullAccess}
        manualOpenKey={syncUnitsRequestKey}
        manualOpenPayload={syncUnitsRequestPayload}
        suppressReminderWidget={folder === 'units'}
      />

      <MobileOverlay
        isVisible={isRightPanelExpanded}
        onClick={() => setIsRightPanelExpanded(false)}
      />

      <DetailsWrapper isExpanded={isRightPanelExpanded}>
        {isRightPanelExpanded && (
          <CollapseButton onClick={() => setIsRightPanelExpanded(false)}>
            <Tooltip title='Collapse panel' placement='left'>
              <ChevronRightIcon />
            </Tooltip>
          </CollapseButton>
        )}

        {isRightPanelExpanded ? (
          <BpInfo redirectPath={backButtonDetails.path} />
        ) : (
          <RibbonButton onClick={() => setIsRightPanelExpanded(true)}>
            <RibbonText>Building Info</RibbonText>
          </RibbonButton>
        )}
      </DetailsWrapper>
    </PageWrapper>
  )
}

export default BpDetails


const PageWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  width: ${({ isRightPanelExpanded }) =>
    isRightPanelExpanded ? 'calc(100vw - 440px)' : 'calc(100vw - 186px)'};
  position: relative;
  // padding-bottom: 50px;
  transition: width 0.3s ease;

  @media (max-width: 1024px) {
    width: 100%;
    padding-right: ${({ isRightPanelExpanded }) =>
      isRightPanelExpanded ? '0' : '40px'};
    flex-direction: column;
  }

  @media (max-width: 768px) {
    padding-right: 0;
    // padding-bottom: 80px;
  }
`

const ContentWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  position: relative;

  @media (max-width: 768px) {
    //padding: 0 16px;
    overflow-x: clip;
  }
`

const DetailsWrapper = styled.div`
  box-sizing: border-box;
  width: ${({ isExpanded }) => (isExpanded ? '280px' : '40px')};
  position: fixed;
  top: ${({ theme }) => theme.dimensions.topBarHeight};
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.lighterGrey};
  padding: ${({ isExpanded }) => (isExpanded ? '25px 20px' : '0')};
  overflow-y: auto;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: ${({ isExpanded, theme }) =>
    isExpanded ? 'none' : `1px solid ${theme.colors.lightGrey}`};
  z-index: 99;

  @media (max-width: 768px) {
    width: ${({ isExpanded }) => (isExpanded ? '100%' : '0px')};
    height: 100vh;
    max-width: 100vw;
    position: fixed;
    top: 0;
    right: 0;
    background-color: ${({ theme }) => theme.colors.white};
    z-index: 9999;
    padding: ${({ isExpanded }) => (isExpanded ? '48px 16px 16px 16px' : '0')};

    overflow-y: auto;
    transition: transform 0.3s ease;
    transform: ${({ isExpanded }) =>
      isExpanded ? 'translateX(0)' : 'translateX(100%)'};
    box-shadow: -5px 0 25px rgba(0, 0, 0, 0.15);
    overscroll-behavior: contain;
  }
`

const RibbonButton = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.lightGrey};
  }
  @media (max-width: 768px) {
    display: none;
  }
`

const RibbonText = styled.div`
  writing-mode: vertical-lr;
  text-orientation: mixed;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  letter-spacing: 1px;
  text-transform: uppercase;
`

const CollapseButton = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  color: ${({ theme }) => theme.colors.haiti};
  z-index: 100;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    background-color: ${({ theme }) => theme.colors.lightGrey};
  }
  svg {
    font-size: 20px;
  }
`


const MobileInfoTrigger = styled.button`
  display: none;
  @media (max-width: 768px) {
    ${floatingButtonStyle}
    display: inline-flex;
    color: ${({ theme }) => theme.colors.primary};
    position: fixed;
    right: 16px;
    z-index: 900;
  }
`

const MobileOverlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
    backdrop-filter: blur(2px);
    touch-action: none;
  }
`


export const MainContentPanel = styled.div`
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.white};
  min-height: 100px;
  padding: 30px 25px;
  box-shadow: 0 2px 9px rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  flex: 1;
  overflow: hidden;
  @media (max-width: 768px) {
    padding: 20px 1px;
  }
`

const OverviewStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`

const ContentCard = styled.div`
  box-sizing: border-box;
  margin-top: 16px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid rgba(12, 18, 41, 0.08);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(12, 18, 41, 0.04);
  padding: 4px 20px 20px;
  min-width: 0;

  @media (max-width: 768px) {
    margin-top: 14px;
    padding: 4px 12px 16px;
    border-radius: 10px;
  }
`

const TabsWrapper = styled.div`
  position: sticky;
  top: var(--passport-bar-bottom, 0px);
  z-index: 30;
  margin: 0 -20px;
  padding: 0 20px;
  background: ${({ theme }) => theme.colors.white};

  @media (max-width: 768px) {
    display: none;
  }
`

const MainContent = styled.div`
  box-sizing: border-box;
  margin: 16px 0 0;
  width: 100%;
  min-width: 0;

  @media (max-width: 768px) {
    padding-bottom: calc(var(--bp-stack-height, 0px) + 72px);
  }
`

export const SectionHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ noMargin }) => (noMargin ? 0 : '20px')};
  ${({ withBorder, theme }) =>
    withBorder &&
    css`
      padding-bottom: 12px;
      border-bottom: 1px solid ${theme.colors.lightGrey};
    `}
`

export const SectionHeader = styled.div`
  font-size: 11px;
  margin-left: 10px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.haiti};
  letter-spacing: 0.3px;
  cursor: ${({ withPointer }) => (withPointer ? 'pointer' : 'default')};

  span {
    display: inline-block;
    font-weight: bold;
    font-size: 15px;
    margin-left: 10px;
  }
`

export const AddButton = styled.div`
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-left: 10px;

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;
      color: ${({ theme }) => theme.colors.grey[600]};
    `}
`

const DraftLabel = styled.div`
  padding: 5px 10px;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.secondary};
  font-size: 14px;
  opacity: 0.7;
`

const BackButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin: 0 0 12px;
`




